/**
 * Helper functions to create parametrized selectors as described in:
 * https://github.com/reduxjs/reselect#q-how-do-i-create-a-selector-that-takes-an-argument
 *
 * Q: How do I create a selector that takes an argument?
 * As shown in the API reference section above, provide input selectors that extract the arguments and forward them
 * to the output selector for calculation:
 *
 * const selectItemsByCategory = createSelector(
 *   [
 *     // Usual first input - extract value from `state`
 *     state => state.items,
 *     // Take the second arg, `category`, and forward to the output selector
 *     (state, category) => category
 *   ],
 *   // Output selector gets (`items, category)` as args
 *   (items, category) => items.filter(item => item.category === category)
 * )
 */

/**
 * Deliberately omitting object and array types: they need special care to avoid unnecessary calculations:
 * https://github.com/reduxjs/reselect#createselectorcreatormemoize-memoizeoptions
 * They certainly require custom memoization
 */
type PrimitiveType =
  | number
  | string
  | boolean
  | bigint
  | symbol
  | undefined
  | null;

export const selectorArg1 =
  <T extends PrimitiveType>() =>
  (state: unknown, arg1: T): T =>
    arg1;
export const selectorArg2 =
  <T extends PrimitiveType>() =>
  (state: unknown, arg1: unknown, arg2: T): T =>
    arg2;
export const selectorArg3 =
  <T extends PrimitiveType>() =>
  (state: unknown, arg1: unknown, arg2: unknown, arg3: T): T =>
    arg3;
export const selectorArg4 =
  <T extends PrimitiveType>() =>
  (state: unknown, arg1: unknown, arg2: unknown, arg3: unknown, arg4: T): T =>
    arg4;

// if you need more than 3 arguments, create another selectorArgXXX function
